import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import { Flex, TextButton, Popover, Text } from "@amzn/storm-ui";
import {
    MultiSelect,
    MultiSelectItem,
    MultiSelectItemGroup,
} from "@amzn/storm-ui-multi-select";
import { cloneDeep, isEqual } from "lodash";
import { type ReactElement, useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useSessionStorage } from "react-use";
import { useStore } from "zustand";

import {
    ACCOUNT_AGNOSTIC_VIEW_WITH_ACCOUNT_SWITCHER,
    UAT_PACKAGED_INSIGHT_SELECTED,
    SINGLE_IDENTIFIER_VIEW,
} from "../../../config/constants";
import { initialUnifiedFilterTypeKeys } from "../../../config/filters/unified";
import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../../constants/telemetry";
import useEncodedUnifiedFilterStateUrl from "../../../hooks/misc/useEncodedUnifiedFilterStateUrl";
import { FeatureFlagsStore } from "../../../store/featureFlags";
import { navigationStore } from "../../../store/navigation";
import { unifiedFilterStore } from "../../../store/unifiedFilters";
import type { AllUnifiedFilterKeys } from "../../../types/filters/unified";
import {
    CoreUnifiedFilterKeys,
    UnifiedFilterKeys,
    UnifiedFilterReadableNames,
} from "../../../types/filters/unified";
import { copyToClipboard } from "../../../utils/clipboard.utils";
import {
    isAccountAgnosticId,
    QueryParam,
} from "../../../utils/permission.utils";
import {
    logWithTakt,
    getUnifiedFilterTelemetryDetails,
} from "../../../utils/telemetry.utils";
import { IcmLink } from "../../external/IcmLink";
import SearchBar from "../filters/SearchBar";
import {
    FilterContainer,
    StyledPill,
} from "../filters/discoveryFilterSharedStyles";
import { QuickFilterDropdown } from "../filters/quickfilters/QuickFilterDropdown";

import { ApplyFiltersButton } from "./ApplyFiltersButton";
import UnifiedFilterButton from "./UnifiedFilterButton";

export const UnifiedFilters = (): ReactElement => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [setEncodedUnifiedFilterStateUrl, getEncodedUnifiedFilterStateUrl] =
        useEncodedUnifiedFilterStateUrl();

    // Get identifier from navigation store
    const [insightIdentifier, setIdentifier] = useStore(
        navigationStore,
        (state) => [state.identifier, state.setIdentifier],
    );
    const enableSingleIdentifierView = !!insightIdentifier;

    const [
        initialVisibleFilters,
        searchQuery,
        deselectAll,
        setSelectedValues,
        filters,
        lastSearchResultTotalCount,
        setIsMetadataDirty,
        setIsContentDirty,
        setLastSearchParameters,
        isLastSearchParametersDifferent,
        setSearchQuery,
        setCurrentQuickFilterId,
    ] = useStore(unifiedFilterStore, (state) => [
        cloneDeep(state.initialVisibleFilters),
        state.searchQuery,
        state.deselectAll,
        state.setSelectedValues,
        state.filters,
        state.lastSearchResultTotalCount,
        state.setIsMetadataDirty,
        state.setIsContentDirty,
        state.setLastSearchParameters,
        state.isLastSearchParametersDifferent,
        state.setSearchQuery,
        state.setCurrentQuickFilterId,
    ]);

    useEffect(() => {
        // On load, if we are in single identifier view, set the search state to think we've performed a search
        if (enableSingleIdentifierView) {
            setIsMetadataDirty(false);
            setIsContentDirty(false);
            setLastSearchParameters();
        }
    }, [
        enableSingleIdentifierView,
        setIsMetadataDirty,
        setIsContentDirty,
        setLastSearchParameters,
    ]);

    // Determine if we show a warning message.
    // Only show if 1. account agnostic view or 2. UAT Packaged Insight flag is on
    const isAccountAgnosticView = isAccountAgnosticId();
    const [isUatPackagedInsight] = useStore(FeatureFlagsStore, (state) => [
        state.uatPackagedInsights,
    ]);

    const UnifiedFilterKeysItems = Object.values(UnifiedFilterKeys);

    const [displayedFilters, setDisplayedFilters] = useState<
        UnifiedFilterKeys[]
    >(initialVisibleFilters);

    // Handler for removing the identifier from store
    const handlePillClose = () => {
        if (enableSingleIdentifierView) {
            // Remove identifier from store
            setIdentifier(undefined);
            setSearchQuery("");
        }
    };

    /**
     * When the user loads a URL with filters already selected, we need to make sure that we display these filters to the user
     */
    useEffect(() => {
        // Skip this effect if we're in single identifier view
        if (enableSingleIdentifierView) {
            return;
        }

        const hiddenFiltersWithSelections = Object.keys(filters).filter(
            (filterKey) => {
                return (
                    !initialVisibleFilters.includes(
                        filterKey as UnifiedFilterKeys,
                    ) &&
                    (filterKey as AllUnifiedFilterKeys) !==
                        CoreUnifiedFilterKeys.TYPE &&
                    filters[filterKey as keyof typeof filters]?.some(
                        (item) => item.isSelected,
                    )
                );
            },
        ) as UnifiedFilterKeys[];

        if (hiddenFiltersWithSelections.length > 0) {
            setDisplayedFilters((prev) => {
                const newFilters = [
                    ...prev,
                    ...hiddenFiltersWithSelections.filter(
                        (f) => !prev.includes(f),
                    ),
                ];
                if (newFilters.length > prev.length) {
                    setIsMetadataDirty(true);
                    setLastSearchParameters();
                }
                return newFilters;
            });
        }
    }, [enableSingleIdentifierView, filters]);

    const shouldShowClearAddedfilterButton = () => {
        // Always enable the button when in single identifier view
        if (enableSingleIdentifierView) {
            return true;
        }

        const allVisibleFilters = [
            ...displayedFilters,
            CoreUnifiedFilterKeys.TYPE,
        ];
        return (
            !isEqual(initialVisibleFilters, displayedFilters) ||
            allVisibleFilters.some((filter) => {
                return filters[filter].some((item) => item.isSelected);
            }) ||
            searchQuery !== ""
        );
    };

    const handleShareFilterClick = () => {
        copyToClipboard(getEncodedUnifiedFilterStateUrl(), "copied-url");
        logWithTakt(TELEMETRY_ID.SHARE_FILTER_CLICK, {
            ...getUnifiedFilterTelemetryDetails(),
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.SHARE_FILTER_CLICK,
            PHOENIX_FEATURE.FILTERS,
            {
                customData: {
                    filters: getUnifiedFilterTelemetryDetails(),
                },
            },
        );
    };

    const removeDisplayedFilter = (filterKey: AllUnifiedFilterKeys) => {
        setDisplayedFilters((prev) => prev.filter((key) => key !== filterKey));
        logWithTakt(TELEMETRY_ID.FILTER_PILL_REMOVED, {
            removed_filter: filterKey,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.FILTER_PILL_REMOVED,
            PHOENIX_FEATURE.FILTERS,
            {
                customData: {
                    removed_filter: filterKey,
                },
            },
        );
    };

    const handleClearFilterClick = () => {
        // Do not want to log with Takt when we are in single identifier view
        if (enableSingleIdentifierView) {
            handlePillClose();
            return;
        }

        // Log with takt first so we preserve the filter context before it was cleared
        logWithTakt(TELEMETRY_ID.CLEAR_ADDED_FILTERS_CLICK, {
            ...getUnifiedFilterTelemetryDetails(),
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.CLEAR_ADDED_FILTERS_CLICK,
            PHOENIX_FEATURE.FILTERS,
            {
                customData: {
                    filters: getUnifiedFilterTelemetryDetails(),
                },
            },
        );

        setSearchQuery("");
        [...displayedFilters].forEach((filter) => deselectAll(filter));

        // Getting the inital type filter state without client pitches and setting it as selected
        const initialUnifiedFilterTypeKeyIds = initialUnifiedFilterTypeKeys.map(
            (x) => x.id,
        );
        setSelectedValues(
            CoreUnifiedFilterKeys.TYPE,
            initialUnifiedFilterTypeKeyIds,
        );

        setCurrentQuickFilterId(undefined);
        setIsMetadataDirty(true);
        setEncodedUnifiedFilterStateUrl();

        // Only refresh the content if clearing the filters has changed our search
        if (isLastSearchParametersDifferent()) {
            setIsContentDirty(true);
        }
    };

    const [hideTooltip, setHideTooltip] = useSessionStorage<boolean>(
        "dontShowTypeFilterTooltipAgain",
        false,
    );
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const isFirstContentLoad = useRef(true);
    useEffect(() => {
        // Skip this effect if we're in single identifier view
        if (enableSingleIdentifierView) {
            return;
        }

        // This condition checks if it is the first contents load with more than 20 results.
        // The purpose is to prevent the popover from showing on page load and only showing after the initial load
        if (isFirstContentLoad.current) {
            isFirstContentLoad.current = lastSearchResultTotalCount < 20;
            return;
        }

        if (!hideTooltip && lastSearchResultTotalCount >= 20) {
            setIsPopoverOpen(true);
        }
    }, [lastSearchResultTotalCount, hideTooltip, enableSingleIdentifierView]);

    return (
        <FilterContainer data-testid="discovery-filter">
            <Flex direction="column" rowGap="base">
                <Flex style={{ width: "420px" }}>
                    <SearchBar />
                </Flex>
                <Flex
                    alignItems="flex-start"
                    rowGap="xsmall"
                    columnGap="xsmall"
                    wrap="wrap"
                    style={{ width: "100%" }}
                >
                    {/*
                The type dropdown should always be visible and ignore the sorting, so render it first
                Additionally, we want to show a popup to nudge users to use the type filters if there
                are more than 20 results
                */}
                    <Popover
                        data-testid="type-filter-tooltip"
                        align="end"
                        isOpen={isPopoverOpen && !enableSingleIdentifierView}
                        position="bottom"
                        disablePortal={true}
                        withCloseButton={true}
                        onCloseButtonClick={() => {
                            setIsPopoverOpen(false);
                            setHideTooltip(true);
                        }}
                        trigger={
                            <UnifiedFilterButton
                                filterKey={CoreUnifiedFilterKeys.TYPE}
                            />
                        }
                    >
                        <Text>
                            <strong>
                                Seeing too many results? Use the Type filter to
                                find specific content types
                            </strong>
                        </Text>
                    </Popover>

                    {enableSingleIdentifierView ? (
                        /* In single identifier view, show only the identifier pill */
                        <StyledPill
                            data-testid="share-single-content-pill"
                            prefix={QueryParam.IDENTIFIER}
                            label={insightIdentifier}
                            onClose={handlePillClose}
                        />
                    ) : (
                        /* In normal view, show all the other filter options */
                        <>
                            {/* Iterate over the filters we want to display and render them */}
                            {displayedFilters
                                .sort((a, b) =>
                                    UnifiedFilterReadableNames[a].localeCompare(
                                        UnifiedFilterReadableNames[b],
                                    ),
                                )
                                .map((key) => (
                                    <UnifiedFilterButton
                                        key={key}
                                        filterKey={key}
                                        onRemoveFilter={removeDisplayedFilter}
                                    />
                                ))}
                            {/* Multi-select to add visible filter pills */}
                            <MultiSelect
                                data-testid="filter-dropdown"
                                onChange={(values) => {
                                    // Deselect the options for any filters that were removed
                                    displayedFilters
                                        .filter(
                                            (filter) =>
                                                !values.includes(filter),
                                        )
                                        .forEach((filter) => {
                                            deselectAll(filter);
                                        });

                                    setDisplayedFilters(
                                        values as UnifiedFilterKeys[],
                                    );
                                    logWithTakt(
                                        TELEMETRY_ID.FILTER_PILL_ADDED,
                                        {
                                            selected_filters: values,
                                        },
                                    );
                                    PhoenixMetrics.emit(
                                        TELEMETRY_ID.FILTER_PILL_ADDED,
                                        PHOENIX_FEATURE.FILTERS,
                                        {
                                            customData: {
                                                selected_filters: values,
                                            },
                                        },
                                    );
                                }}
                                name={"FilterDropdown"}
                                selectedValues={displayedFilters}
                                onOverrideLabel={() => "Add filter"}
                                transparentButton={true}
                            >
                                <MultiSelectItemGroup label="Add Filter">
                                    {UnifiedFilterKeysItems.sort((a, b) =>
                                        UnifiedFilterReadableNames[
                                            a
                                        ].localeCompare(
                                            UnifiedFilterReadableNames[b],
                                        ),
                                    ).map((key) => {
                                        return (
                                            <MultiSelectItem
                                                key={key}
                                                value={key}
                                                name={key}
                                                data-testid="filter-type-option"
                                            >
                                                {
                                                    UnifiedFilterReadableNames[
                                                        key
                                                    ]
                                                }
                                            </MultiSelectItem>
                                        );
                                    })}
                                </MultiSelectItemGroup>
                            </MultiSelect>
                        </>
                    )}

                    {shouldShowClearAddedfilterButton() && (
                        <TextButton
                            style={{ marginTop: 5 }}
                            onClick={handleClearFilterClick}
                            disabled={!shouldShowClearAddedfilterButton()}
                            data-testid="remove-all-filters-button"
                        >
                            Clear added filters
                        </TextButton>
                    )}
                    <TextButton
                        style={{ marginTop: 5 }}
                        onClick={handleShareFilterClick}
                        data-testid="share-filters-button"
                    >
                        Share contents
                    </TextButton>

                    <Flex
                        style={{ marginLeft: "auto" }}
                        direction="row"
                        columnGap="xxxsmall"
                    >
                        <Flex columnGap="small">
                            <div style={{ marginTop: 5 }}>
                                <IcmLink />
                            </div>
                            <div data-tour-id="quick-filter-dropdown">
                                <QuickFilterDropdown />
                            </div>
                        </Flex>
                        {/* Only show Apply Filters button in normal view */}
                        {!enableSingleIdentifierView && <ApplyFiltersButton />}
                    </Flex>
                </Flex>
            </Flex>
            <Text
                color="default"
                withMargin={true}
                weight="bold"
                hidden={!isAccountAgnosticView || enableSingleIdentifierView}
            >
                {ACCOUNT_AGNOSTIC_VIEW_WITH_ACCOUNT_SWITCHER}
            </Text>
            <Text
                color="default"
                withMargin={true}
                weight="bold"
                hidden={!isUatPackagedInsight || enableSingleIdentifierView}
            >
                {UAT_PACKAGED_INSIGHT_SELECTED}
            </Text>
            <Text
                color="default"
                size="base"
                withMargin={true}
                weight="bold"
                hidden={!enableSingleIdentifierView}
            >
                {SINGLE_IDENTIFIER_VIEW}
            </Text>
        </FilterContainer>
    );
};
